// Brand mark — inline SVG recreation of the Street Forensic Services
// logo, shared shape with the public site (packages/site BrandMark).
// Pure vector so it stays crisp at any size. Wordmark colors come from
// CSS (.brand-word / .brand-word-accent) so the mark adapts to light or
// dark surfaces; "Services" stays cyan as the brand anchor.
//
// Renders at intrinsic 360×200 and is sized by the caller via CSS
// (.app-brand .brand-mark height). Width auto-scales via viewBox.

interface BrandMarkProps {
  /** Compact size for app shells / footers. */
  small?: boolean;
  className?: string;
}

export function BrandMark({ small = false, className }: BrandMarkProps): JSX.Element {
  return (
    <svg
      viewBox="0 0 360 200"
      className={`brand-mark ${small ? 'brand-mark-sm' : ''} ${className ?? ''}`}
      role="img"
      aria-label="Street Forensic Services"
      preserveAspectRatio="xMidYMid meet"
    >
      <defs>
        <linearGradient id="bm-rim" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%"  stopColor="#e3ecf0" />
          <stop offset="45%" stopColor="#8c9ba6" />
          <stop offset="100%" stopColor="#dfe8ed" />
        </linearGradient>

        <radialGradient id="bm-lens" cx="0.38" cy="0.34" r="0.85">
          <stop offset="0%"   stopColor="#1a4675" />
          <stop offset="55%"  stopColor="#0a2540" />
          <stop offset="100%" stopColor="#04111f" />
        </radialGradient>

        <linearGradient id="bm-road" x1="0.5" y1="0" x2="0.5" y2="1">
          <stop offset="0%"   stopColor="#0a2540" />
          <stop offset="100%" stopColor="#1a4575" />
        </linearGradient>
      </defs>

      {/* ----------- Magnifying glass ----------- */}
      <g transform="translate(106 124) rotate(45)">
        <rect
          x="0" y="-9"
          width="56" height="18"
          rx="4"
          fill="url(#bm-rim)"
          stroke="#0a2540"
          strokeWidth="1.2"
        />
        <rect x="8" y="-2" width="40" height="4" rx="2" fill="#00f5ff" opacity="0.65" />
      </g>

      <circle cx="70" cy="90" r="62" fill="url(#bm-rim)" />
      <circle cx="70" cy="90" r="53" fill="url(#bm-lens)" />
      <ellipse cx="50" cy="68" rx="22" ry="11" fill="#ffffff" opacity="0.06" />

      <clipPath id="bm-lens-clip">
        <circle cx="70" cy="90" r="53" />
      </clipPath>
      <g clipPath="url(#bm-lens-clip)" stroke="#5ee3ff" strokeWidth="1.1" fill="none" strokeLinecap="round" opacity="0.85">
        <path d="M22 60 H38 V52 H46" />
        <path d="M22 78 H34" />
        <path d="M20 96 H30 V108" />
        <path d="M118 60 H102 V52 H94" />
        <path d="M118 78 H106" />
        <path d="M120 96 H110 V108" />
      </g>
      <g clipPath="url(#bm-lens-clip)" fill="#00f5ff">
        <circle cx="22" cy="60" r="1.5" />
        <circle cx="46" cy="52" r="1.5" />
        <circle cx="34" cy="78" r="1.5" />
        <circle cx="30" cy="108" r="1.5" />
        <circle cx="118" cy="60" r="1.5" />
        <circle cx="94" cy="52" r="1.5" />
        <circle cx="106" cy="78" r="1.5" />
        <circle cx="110" cy="108" r="1.5" />
      </g>

      <g clipPath="url(#bm-lens-clip)">
        <path d="M40 142 L70 64 L100 142 Z" fill="url(#bm-road)" />
        <radialGradient id="bm-vp" cx="0.5" cy="0.2" r="0.5">
          <stop offset="0%" stopColor="#7bf0ff" stopOpacity="0.8" />
          <stop offset="100%" stopColor="#7bf0ff" stopOpacity="0" />
        </radialGradient>
        <circle cx="70" cy="64" r="20" fill="url(#bm-vp)" />
        <g fill="#7bf0ff">
          <rect x="68.5" y="78"  width="3"  height="6"  rx="0.5" />
          <rect x="67.5" y="92"  width="5"  height="9"  rx="0.5" />
          <rect x="66"   y="108" width="8"  height="11" rx="1"   />
          <rect x="63.5" y="126" width="13" height="14" rx="1.2" />
        </g>
      </g>

      {/* ----------- Wordmark ----------- */}
      <g
        fontFamily="var(--font-serif, 'Playfair Display'), Georgia, serif"
        fontWeight="700"
        textAnchor="start"
      >
        <text x="170" y="68"  fontSize="44" className="brand-word">Street</text>
        <text x="170" y="116" fontSize="44" className="brand-word">Forensic</text>
        <text x="170" y="164" fontSize="44" fill="#00f5ff" className="brand-word-accent">Services</text>
      </g>
    </svg>
  );
}
